import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Heart, AlertTriangle, Clock, ChevronRight, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useClientFollowupList } from "@/hooks/useClientFollowup";
import { PiktableLoader } from "@/components/PiktableLoader";

const healthLabels: Record<string, { label: string; cls: string }> = {
  ok: { label: "OK", cls: "bg-green-500/10 text-green-700 border-green-500/30" },
  watch: { label: "À surveiller", cls: "bg-amber-500/10 text-amber-700 border-amber-500/30" },
  risk: { label: "Risque", cls: "bg-red-500/10 text-red-700 border-red-500/30" },
  to_evaluate: { label: "À évaluer", cls: "bg-muted text-muted-foreground border-border" },
};

const priorityLabels: Record<string, { label: string; cls: string }> = {
  high: { label: "Haute", cls: "bg-red-500/10 text-red-700 border-red-500/30" },
  normal: { label: "Normale", cls: "bg-blue-500/10 text-blue-700 border-blue-500/30" },
  low: { label: "Basse", cls: "bg-muted text-muted-foreground border-border" },
};

export default function ClientFollowupList() {
  const navigate = useNavigate();
  const { data, isLoading } = useClientFollowupList();
  const [search, setSearch] = useState("");
  const [healthFilter, setHealthFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [hideInactive, setHideInactive] = useState(true);

  const filtered = useMemo(() => {
    const now = new Date();
    return (data || []).filter((row) => {
      if (hideInactive && !row.is_active) return false;
      if (search && !row.name.toLowerCase().includes(search.toLowerCase()) && !(row.city || "").toLowerCase().includes(search.toLowerCase())) return false;
      if (healthFilter !== "all" && row.client_health !== healthFilter) return false;
      if (priorityFilter !== "all" && row.priority !== priorityFilter) return false;
      if (overdueOnly) {
        if (!row.next_action_at) return false;
        if (new Date(row.next_action_at) > now) return false;
      }
      return true;
    });
  }, [data, search, healthFilter, priorityFilter, overdueOnly, hideInactive]);

  if (isLoading) return <PiktableLoader message="Chargement..." />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background">
      <div className="container mx-auto p-6 max-w-7xl space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/admin")} aria-label="Retour">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-serif font-bold flex items-center gap-3">
                <Heart className="w-8 h-8 text-primary" />
                Suivi clients
              </h1>
              <p className="text-muted-foreground mt-1">
                Suivi commercial et terrain de tous les établissements
              </p>
            </div>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Filtres</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative md:col-span-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher un établissement ou une ville..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={healthFilter} onValueChange={setHealthFilter}>
                <SelectTrigger><SelectValue placeholder="Santé" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes santés</SelectItem>
                  <SelectItem value="ok">OK</SelectItem>
                  <SelectItem value="watch">À surveiller</SelectItem>
                  <SelectItem value="risk">Risque</SelectItem>
                </SelectContent>
              </Select>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger><SelectValue placeholder="Priorité" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes priorités</SelectItem>
                  <SelectItem value="high">Haute</SelectItem>
                  <SelectItem value="normal">Normale</SelectItem>
                  <SelectItem value="low">Basse</SelectItem>
                </SelectContent>
              </Select>
              <div className="md:col-span-4 flex flex-wrap gap-2">
                <Button
                  variant={overdueOnly ? "default" : "outline"}
                  size="sm"
                  onClick={() => setOverdueOnly((v) => !v)}
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Actions en retard uniquement
                </Button>
                <Button
                  variant={hideInactive ? "default" : "outline"}
                  size="sm"
                  onClick={() => setHideInactive((v) => !v)}
                >
                  Masquer les inactifs
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Établissement</TableHead>
                  <TableHead>Ville</TableHead>
                  <TableHead>Dernier contact</TableHead>
                  <TableHead>Prochaine action</TableHead>
                  <TableHead>Santé</TableHead>
                  <TableHead>Priorité</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((row) => {
                  const overdue = row.next_action_at && new Date(row.next_action_at) < new Date();
                  return (
                    <TableRow
                      key={row.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/admin/client-followup/${row.id}`)}
                    >
                      <TableCell className="font-medium">
                        <span className="flex items-center gap-2">
                          {row.name}
                          {!row.is_active && (
                            <Badge variant="outline" className="bg-muted text-muted-foreground border-border text-xs">
                              Inactif
                            </Badge>
                          )}
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{row.city || "—"}</TableCell>
                      <TableCell>
                        {row.last_visit_date
                          ? new Date(row.last_visit_date).toLocaleDateString("fr-CH")
                          : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>
                        {row.next_action_at ? (
                          <span className={overdue ? "text-red-600 font-medium flex items-center gap-1" : ""}>
                            {overdue && <Clock className="w-3 h-3" />}
                            {new Date(row.next_action_at).toLocaleString("fr-CH", { dateStyle: "short", timeStyle: "short" })}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.client_health ? (
                          <Badge variant="outline" className={healthLabels[row.client_health].cls}>
                            {healthLabels[row.client_health].label}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.priority ? (
                          <Badge variant="outline" className={priorityLabels[row.priority].cls}>
                            {priorityLabels[row.priority].label}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      Aucun établissement ne correspond aux filtres
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
